import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Grid,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  Container,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  alpha,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import configurationApiService, {
  SystemConfiguration,
} from "../../services/configuration-api";
import ReimbursementCategoriesCard, {
  ReimbursementCategoriesCardRef,
} from "../../components/Configuration/ReimbursementCategoriesCard";
import TaxClassificationCard, {
  TaxClassificationCardRef,
} from "../../components/Configuration/TaxClassificationCard";
import OfficeLocationsCard, {
  OfficeLocationsCardRef,
} from "../../components/Configuration/OfficeLocationsCard";
import HolidaysCard, {
  HolidaysCardRef,
} from "../../components/Configuration/HolidaysCard";
import WorkdayOverridesCard, {
  WorkdayOverridesCardRef,
} from "../../components/Configuration/WorkdayOverridesCard";
import ExternalLinksCard, {
  ExternalLinksCardRef,
} from "../../components/Configuration/ExternalLinksCard";
import CompanyCalendarEventsCard, {
  CompanyCalendarEventsCardRef,
} from "../../components/Configuration/CompanyCalendarEventsCard";
import AnnouncementsCard, {
  AnnouncementsCardRef,
} from "../../components/Configuration/AnnouncementsCard";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import UploadIcon from "@mui/icons-material/Upload";
import CampaignIcon from "@mui/icons-material/Campaign";

// Import local icons
import notificationIcon from "../../assets/icons/notification.svg";
import locationIcon from "../../assets/icons/location.svg";
import linkIcon from "../../assets/icons/link.svg";
import calendarIcon from "../../assets/icons/calendar.svg";
import eventIcon from "../../assets/icons/event.svg";
import plannerIcon from "../../assets/icons/planner.svg";
import taxIcon from "../../assets/icons/tax.svg";
import accountIcon from "../../assets/icons/account.svg";

const ConfigurationPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [configurations, setConfigurations] = useState<SystemConfiguration[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAccessToken, setShowAccessToken] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);

  // Track which panels have been loaded to avoid redundant API calls
  const [loadedPanels, setLoadedPanels] = useState<Set<string>>(new Set());

  // Refs to trigger add dialogs in child components
  const officeLocationsCardRef = React.useRef<OfficeLocationsCardRef>(null);
  const externalLinksCardRef = React.useRef<ExternalLinksCardRef>(null);
  const holidaysCardRef = React.useRef<HolidaysCardRef>(null);
  const calendarEventsCardRef =
    React.useRef<CompanyCalendarEventsCardRef>(null);
  const workdayOverridesCardRef = React.useRef<WorkdayOverridesCardRef>(null);
  const taxClassificationCardRef = React.useRef<TaxClassificationCardRef>(null);
  const reimbursementCategoriesCardRef =
    React.useRef<ReimbursementCategoriesCardRef>(null);
  const announcementsCardRef = React.useRef<AnnouncementsCardRef>(null);

  // Load configurations on mount
  useEffect(() => {
    loadConfigurations();
  }, []);

  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await configurationApiService.getAllConfigurations();
      setConfigurations(data);
      setHasChanges(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("common.error") || "Error loading configurations",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (configKey: string, newValue: string) => {
    setConfigurations((prev) =>
      prev.map((config) =>
        config.configKey === configKey
          ? { ...config, configValue: newValue }
          : config,
      ),
    );
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await configurationApiService.updateConfigurations(configurations);
      setSuccess(t("common.success") || "Configurations saved successfully");
      setHasChanges(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("common.error") || "Error saving configurations",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = () => {
    loadConfigurations();
  };

  // Helper functions - must be defined before useMemo
  const isRoomIdConfig = (configKey: string): boolean => {
    return configKey.startsWith("ELEMENT_ROOM_ID_");
  };

  const getConfigLabel = (configKey: string): string => {
    const labels: { [key: string]: string } = {
      TELEWORK_LINK: t("configurations.labelTeleworkLink"),
      ELEMENT_ACCESS_TOKEN: t("configurations.labelAccessToken"),
      ELEMENT_ROOM_ID_REIMBURSEMENT: t("configurations.labelReimbursement"),
      ELEMENT_ROOM_ID_LEAVE: t("configurations.labelLeaveManagement"),
      ELEMENT_ROOM_ID_TIME_TRACKING: t("configurations.labelTimeTracking"),
      ELEMENT_ROOM_ID_EXTERNAL_SUPPORT: t(
        "configurations.labelExternalSupport",
      ),
      ELEMENT_ROOM_ID_ANNOUNCEMENT: t("configurations.labelAnnouncement"),
    };
    return labels[configKey] || configKey;
  };

  // Memoize filtered configurations to prevent expensive re-renders
  const teleworkConfig = useMemo(
    () => configurations.filter((c) => c.configKey === "TELEWORK_LINK"),
    [configurations],
  );

  const elementAccessTokenConfig = useMemo(
    () => configurations.filter((c) => c.configKey === "ELEMENT_ACCESS_TOKEN"),
    [configurations],
  );

  const roomIdConfigs = useMemo(
    () => configurations.filter((c) => isRoomIdConfig(c.configKey)),
    [configurations],
  );

  const formatDateTime = (dateValue: any): string => {
    if (!dateValue) return t("configurations.never");

    // Handle array format [year, month, day, hour, minute, second, nanoseconds]
    if (Array.isArray(dateValue)) {
      const [year, month, day, hour, minute, second] = dateValue;
      const date = new Date(year, month - 1, day, hour, minute, second);
      return date.toLocaleString();
    }

    // Handle string format
    if (typeof dateValue === "string") {
      const date = new Date(dateValue);
      return date.toLocaleString();
    }

    return t("configurations.invalidDate");
  };

  const handleClickShowAccessToken = () => {
    setShowAccessToken(!showAccessToken);
  };

  const handleMouseDownAccessToken = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();
  };

  const handleAccordionChange = (panel: string) => {
    setExpandedPanels((prev) => {
      const isExpanding = !prev.includes(panel);

      // If expanding and not yet loaded, mark as loaded
      if (isExpanding && !loadedPanels.has(panel)) {
        setLoadedPanels((prevLoaded) => new Set([...prevLoaded, panel]));
      }

      return prev.includes(panel)
        ? prev.filter((p) => p !== panel)
        : [...prev, panel];
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 1 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t("configurations.pageTitle")}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t("common.refresh")}>
            <IconButton onClick={handleRefresh} disabled={loading || saving}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Accordion Sections */}
      <Stack spacing={2}>
        {/* 1. Element Notification Configuration & Telework Link */}
        <Accordion
          expanded={expandedPanels.includes("panel1")}
          onChange={() => handleAccordionChange("panel1")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={notificationIcon}
              alt={t("configurations.section1.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section1.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section1.subtitle")}
              </Typography>
            </Box>
            <Box
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
                size="small"
                sx={{ mr: 1 }}
              >
                {saving ? t("common.saving") : t("common.save")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            {/* Access Token Section */}
            {elementAccessTokenConfig.map((config) => (
              <Box key={config.configKey} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("configurations.section1.accessToken")}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  {t("configurations.section1.accessTokenDesc")}
                </Typography>
                <TextField
                  fullWidth
                  value={config.configValue}
                  onChange={(e) =>
                    handleConfigChange(config.configKey, e.target.value)
                  }
                  placeholder={t(
                    "configurations.section1.accessTokenPlaceholder",
                  )}
                  type={showAccessToken ? "text" : "password"}
                  disabled={saving}
                  variant="outlined"
                  size="small"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t(
                              "configurations.section1.toggleVisibility",
                            )}
                            onClick={handleClickShowAccessToken}
                            onMouseDown={handleMouseDownAccessToken}
                            edge="end"
                          >
                            {showAccessToken ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            ))}

            {/* Room IDs Section */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                {t("configurations.section1.roomIds")}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "block", mb: 1 }}
              >
                {t("configurations.section1.roomIdsNote")}
              </Typography>

              <Grid container spacing={2}>
                {roomIdConfigs.map((config) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={config.configKey}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ mb: 0.5, fontWeight: 500 }}
                      >
                        {getConfigLabel(config.configKey)}
                      </Typography>
                      <TextField
                        fullWidth
                        value={config.configValue}
                        onChange={(e) =>
                          handleConfigChange(config.configKey, e.target.value)
                        }
                        placeholder={`${t("configurations.section1.enterRoomId")} ${getConfigLabel(config.configKey)}`}
                        disabled={saving}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
            <Divider sx={{ my: 3 }} />
            {/* Telework Link Section */}
            {teleworkConfig.map((config) => (
              <Box key={config.configKey}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t("configurations.section1.teleworkLink")}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block", mb: 2 }}
                >
                  {t("configurations.section1.teleworkLinkDesc")}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={config.configValue}
                  onChange={(e) =>
                    handleConfigChange(config.configKey, e.target.value)
                  }
                  placeholder={t(
                    "configurations.section1.teleworkLinkPlaceholder",
                  )}
                  disabled={saving}
                  variant="outlined"
                  size="small"
                />
                {config.updatedAt && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ display: "block", mt: 2 }}
                  >
                    {t("configurations.lastUpdated")}:{" "}
                    {formatDateTime(config.updatedAt)} {t("configurations.by")}{" "}
                    {config.updatedBy}
                  </Typography>
                )}
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>

        {/* 2. Office Locations */}
        <Accordion
          expanded={expandedPanels.includes("panel2")}
          onChange={() => handleAccordionChange("panel2")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={locationIcon}
              alt={t("configurations.section2.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section2.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section2.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  officeLocationsCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <OfficeLocationsCard
              ref={officeLocationsCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel2")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 3. External Links */}
        <Accordion
          expanded={expandedPanels.includes("panel3")}
          onChange={() => handleAccordionChange("panel3")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={linkIcon}
              alt={t("configurations.section3.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section3.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section3.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  externalLinksCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <ExternalLinksCard
              ref={externalLinksCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel3")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 4. Holidays */}
        <Accordion
          expanded={expandedPanels.includes("panel4")}
          onChange={() => handleAccordionChange("panel4")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={calendarIcon}
              alt={t("configurations.section4.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section4.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section4.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    holidaysCardRef.current?.openAddDialog();
                  }}
                  size="small"
                >
                  {t("common.add")}
                </Button>
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <HolidaysCard
              ref={holidaysCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel4")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 5. Company Calendar Events */}
        <Accordion
          expanded={expandedPanels.includes("panel5")}
          onChange={() => handleAccordionChange("panel5")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={eventIcon}
              alt={t("configurations.section5.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section5.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section5.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  calendarEventsCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <CompanyCalendarEventsCard
              ref={calendarEventsCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel5")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 6. Workday Overrides */}
        <Accordion
          expanded={expandedPanels.includes("panel6")}
          onChange={() => handleAccordionChange("panel6")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={plannerIcon}
              alt={t("configurations.section6.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section6.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section6.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  workdayOverridesCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <WorkdayOverridesCard
              ref={workdayOverridesCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel6")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 7. Tax Classifications */}
        <Accordion
          expanded={expandedPanels.includes("panel7")}
          onChange={() => handleAccordionChange("panel7")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={taxIcon}
              alt={t("configurations.section7.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section7.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section7.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  taxClassificationCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <TaxClassificationCard
              ref={taxClassificationCardRef}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel7")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 8. Debit Accounts */}
        <Accordion
          expanded={expandedPanels.includes("panel8")}
          onChange={() => handleAccordionChange("panel8")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <Box
              component="img"
              src={accountIcon}
              alt={t("configurations.section8.title")}
              sx={{ width: 40, height: 40 }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section8.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section8.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  reimbursementCategoriesCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <ReimbursementCategoriesCard
              ref={reimbursementCategoriesCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel8")}
            />
          </AccordionDetails>
        </Accordion>

        {/* 9. Announcements */}
        <Accordion
          expanded={expandedPanels.includes("panel9")}
          onChange={() => handleAccordionChange("panel9")}
          sx={{
            borderRadius: 2,
            "&:before": { display: "none" },
            boxShadow: theme.shadows[2],
            "&.Mui-expanded": {
              boxShadow: theme.shadows[4],
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
              borderRadius: 2,
              "&.Mui-expanded": {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              },
              "& .MuiAccordionSummary-content": {
                alignItems: "center",
                gap: 2,
              },
            }}
          >
            <CampaignIcon
              sx={{ width: 40, height: 40, color: theme.palette.info.main }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("configurations.section9.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("configurations.section9.subtitle")}
              </Typography>
            </Box>
            <Box sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  announcementsCardRef.current?.openAddDialog();
                }}
                size="small"
              >
                {t("common.add")}
              </Button>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 3 }}>
            <AnnouncementsCard
              ref={announcementsCardRef}
              onSave={handleRefresh}
              hideHeader={true}
              shouldLoad={loadedPanels.has("panel9")}
            />
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Container>
  );
};

export default ConfigurationPage;
